import {
  Dimmer,
  DimmerDimmable,
  Divider,
  Form,
  Loader,
  Modal,
  Segment,
} from '@mairistem/composants';
import _ from 'lodash';
import React from 'react';
import { toast } from 'react-toastify';

import {
  BoutonsValidationEncaissement,
  ButtonAjoutReglement,
  CommentairesEncaissement,
  MessageDepassementTitres,
} from '@/components/Encaissement/ModalEncaissementCommun';
import { ENCAISSEMENT } from '@/constants';
// Import par chemin direct et non via le barrel `@/hooks` : les tests du panel comptant
// mockent `@/hooks` de façon exhaustive.
import useReglementsEncaissement from '@/hooks/useReglementsEncaissement';
import type { PayeurType } from '@/types';
import {
  avecCodeModeReglement,
  formatPayeurLabel,
  formatReglementBody,
  repartirAuProrata,
  verifierEligibiliteTitres,
} from '@/utils';
import { getMessageErreurEncaissement } from '@/utils/apiErrors';
import { useRegieStore } from '@/zustand';
import ComptantTable from '../Fiche/Panel/ComptantTable';
import type { LigneComptant } from '../Fiche/Panel/comptant';
import { ModalEncaissementReglements } from '../ModalEncaissement/ModalEncaissementReglements';

interface ModalEncaissementComptantProps {
  payeur: PayeurType;
  lignes: LigneComptant[];
  total: number;
  // Affiche ou non le bouton d'envoi du reçu par mail
  envoiRecuParMail?: boolean;
  // Callback a la validation de la modal
  onSave: (
    body: Record<string, unknown>,
    imprimerRecu?: boolean,
    envoyerMail?: boolean,
  ) => Promise<void>;
  // Callback à la fermeture de la modal
  onClose: () => void;
}

/**
 * Encaissement au comptant : aucune facture n'existe au préalable.
 *
 * Reprend le tableau saisi dans le panel (figé, en lecture seule) et la saisie des règlements
 * de l'encaissement de facture. Deux différences volontaires avec cette dernière : l'encaissement
 * est nécessairement total (pas de reçu partiel), et la répartition d'un règlement sur les lignes
 * est toujours faite au prorata (aucune répartition manuelle).
 */
const ModalEncaissementComptant = (props: ModalEncaissementComptantProps) => {
  const { payeur, lignes, total, envoiRecuParMail = false, onSave, onClose } = props;

  const encaissementPossible = useRegieStore((state) => state.regie?.encaissementPossible);

  // Etat de chargement
  const [loading, setLoading] = React.useState<boolean>(false);
  // Commentaire du reçu
  const [commentaire, setCommentaire] = React.useState<string>('');
  // Commentaire du payeur
  const [commentairePayeur, setCommentairePayeur] = React.useState<string>(payeur?.commentaire);

  const {
    reglements,
    filteredModesReglements,
    reglementsComplet,
    resteModeReglementDisponible,
    montantNumeraire,
    resteReglements,
    handleReglementChange,
    handleReglementReset,
    handleReglementMultipleDelete,
    handleReglementMultipleAdd,
    getModesReglementsDesactives,
  } = useReglementsEncaissement(total);

  /**
   * Lignes de la facture à créer, au format de l'API. Elles n'existent pas encore : elles sont
   * désignées par leur numéro d'ordre, que l'API remplacera par les identifiants réellement créés.
   */
  const lignesBody = React.useMemo(
    () =>
      _.map(lignes, (ligne, index) => {
        const montant = parseFloat((ligne.quantite * ligne.produit.prixUnitaireTTC).toFixed(2));
        const montantHT = parseFloat((ligne.quantite * ligne.produit.prixUnitaireHT).toFixed(2));

        return {
          numeroOrdre: index + 1,
          identifiantProduit: ligne.produit.identifiant,
          quantite: ligne.quantite,
          montantHT,
          montantTVA: parseFloat((montant - montantHT).toFixed(2)),
          montant,
        };
      }),
    [lignes],
  );

  // Lignes servies par la répartition ; leur produit porte l'éligibilité aux titres de paiement.
  const lignesRepartition = React.useMemo(
    () =>
      _.map(lignesBody, (ligne, index) => ({
        cle: ligne.numeroOrdre,
        montant: ligne.montant,
        produit: lignes[index].produit,
      })),
    [lignes, lignesBody],
  );

  // Règlements complétés du code de leur mode, pour réserver les titres aux produits éligibles.
  const reglementsRepartition = React.useMemo(
    () => avecCodeModeReglement(reglements, filteredModesReglements),
    [reglements, filteredModesReglements],
  );

  // Titres de paiement supérieurs au total des produits éligibles : la validation est bloquée.
  const depassementsTitres = React.useMemo(
    () => verifierEligibiliteTitres(lignesRepartition, reglementsRepartition),
    [lignesRepartition, reglementsRepartition],
  );

  /** L'encaissement au comptant n'autorise pas de reçu partiel : les règlements doivent
   * couvrir exactement le total à encaisser. */
  const isValidateDisabled = React.useMemo(
    () =>
      encaissementPossible === false ||
      !reglementsComplet ||
      resteReglements !== 0 ||
      montantNumeraire > ENCAISSEMENT.PLAFOND_NUMERAIRE ||
      !_.isEmpty(depassementsTitres) ||
      loading,
    [
      encaissementPossible,
      reglementsComplet,
      resteReglements,
      montantNumeraire,
      depassementsTitres,
      loading,
    ],
  );

  // S'il y a au moins un reglement numéraire, on n'autorise pas de faire un encaissement sans reçu
  const isSansRecuDisabled = React.useMemo(() => {
    if (montantNumeraire > 0 && process.env.NODE_ENV !== 'test') {
      return true;
    }

    return isValidateDisabled;
  }, [montantNumeraire, isValidateDisabled]);

  const isValidateButtonPositive = React.useMemo(
    () => reglementsComplet && resteReglements === 0 && _.isEmpty(depassementsTitres),
    [reglementsComplet, resteReglements, depassementsTitres],
  );

  const libellePayeur = React.useMemo(() => formatPayeurLabel(payeur ?? undefined), [payeur]);

  /**
   * Construit le corps de la requête d'encaissement au comptant.
   */
  const getBodyEncaissement = React.useCallback(() => {
    // Les titres de paiement ne sont affectés qu'aux produits éligibles.
    const tabCalculeProrata = repartirAuProrata(lignesRepartition, reglementsRepartition, total);

    return {
      // !TODO : Gestion documents
      identifiantDocInfo: 1,
      identifiantPayeur: payeur?.identifiant,
      identifiantAdresse: payeur?.adresse?.identifiant,
      commentaire,
      commentairePayeur,
      lignes: lignesBody,
      // On le reverse pour que le premier règlement saisi ressorte en premier dans les reçus
      reglements: _.reverse(
        _.map(reglements, (reglement) => ({
          ...formatReglementBody(reglement, payeur?.identifiant),
          lignes: _.map(lignesBody, (ligne) => ({
            numeroOrdre: ligne.numeroOrdre,
            montantReglement: tabCalculeProrata[ligne.numeroOrdre][reglement.identifiant],
          })),
        })),
      ),
    };
  }, [
    lignesBody,
    lignesRepartition,
    payeur,
    reglements,
    reglementsRepartition,
    total,
    commentaire,
    commentairePayeur,
  ]);

  /**
   * Gère la validation de l'encaissement.
   *
   * @param {string} param - 'print' pour imprimer le reçu, 'mail' pour l'envoyer par mail.
   */
  const handleValidate = React.useCallback(
    (param?: string) => {
      setLoading(true);

      onSave(getBodyEncaissement(), param === 'print', param === 'mail')
        .catch((error) => {
          toast(getMessageErreurEncaissement(error), { type: 'error' });
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [getBodyEncaissement, onSave],
  );

  return (
    <Modal open closeIcon {...(!loading && { onClose })} size="huge">
      <Modal.Header>Encaissement au comptant</Modal.Header>
      <Modal.Content>
        <DimmerDimmable as={Form} dimmed={loading} style={{ width: '100%', minHeight: 150 }}>
          <Dimmer active={loading} inverted>
            <Loader inverted content="Encaissement en cours" />
          </Dimmer>
          <ComptantTable readOnly payeur={payeur} lignes={lignes} total={total} />
          <ModalEncaissementReglements
            reglements={reglements}
            payeur={libellePayeur}
            reste={total}
            reglementsComplet={reglementsComplet}
            onReglementMultipleDelete={handleReglementMultipleDelete}
            onReglementChange={handleReglementChange}
            onReglementReset={handleReglementReset}
            addButtonVisible={resteModeReglementDisponible}
            getModesReglementsDesactives={getModesReglementsDesactives}
          >
            <ButtonAjoutReglement
              testId="button-add-reglement"
              visible={
                _.size(reglements) < ENCAISSEMENT.NOMBRE_REGLEMENTS_MAX &&
                total !== resteReglements &&
                resteReglements > 0 &&
                reglementsComplet
              }
              maximumAtteint={_.size(reglements) === ENCAISSEMENT.NOMBRE_REGLEMENTS_MAX}
              onAdd={handleReglementMultipleAdd}
            />
          </ModalEncaissementReglements>
          <MessageDepassementTitres depassements={depassementsTitres} />
          <Divider />
          <Segment basic style={{ marginTop: 0, paddingBottom: 0, paddingRight: 0 }}>
            <CommentairesEncaissement
              commentaire={commentaire}
              onCommentaireChange={setCommentaire}
              commentairePayeur={commentairePayeur}
              onCommentairePayeurChange={setCommentairePayeur}
              commentairePayeurInitial={payeur?.commentaire}
            />
          </Segment>
        </DimmerDimmable>
      </Modal.Content>
      <Modal.Actions>
        <BoutonsValidationEncaissement
          loading={loading}
          positive={isValidateButtonPositive}
          onValidate={handleValidate}
          sansRecuVisible
          sansRecuDisabled={isSansRecuDisabled}
          avecRecuDisabled={isValidateDisabled}
          mailVisible={envoiRecuParMail}
          onClose={onClose}
          retourContent="Retour à la saisie"
          testIds={{
            retour: 'button-cancel-encaissement',
            sansRecu: 'button-validate-sans-recu',
            mail: 'button-validate-avec-recu-mail',
            print: 'button-validate-avec-recu-print',
          }}
        />
      </Modal.Actions>
    </Modal>
  );
};

export default ModalEncaissementComptant;
